import React, { useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Icon, UiDropdown, UiDropdownSearchList, UiText } from '@shared/ui';

import cls from './InventoryCard.module.css';
import { useEquipmentInventoryStatuses } from '../../lib/hooks/useEquipmentInventoryStatuses';

import type { EquipmentInventoryStatus } from '../../model';
import type { UiDropdownSearchListItem } from '@shared/ui';

interface InventoryCardActionsProps {
    status: EquipmentInventoryStatus;
    warehouses?: UiDropdownSearchListItem[];
    hasFullAccess?: boolean;
    canMove?: boolean;
    onCopyClick?: React.MouseEventHandler<HTMLLIElement>;
    onDeleteClick?: React.MouseEventHandler<HTMLLIElement>;
    onMoveClick?: (warehouse: UiDropdownSearchListItem, closeDropdown: () => void) => void;
    onStatusClick?: (
        status: EquipmentInventoryStatus
    ) => React.MouseEventHandler<HTMLLIElement> | undefined;
}

const inventoryStatusIconNames: Record<EquipmentInventoryStatus, string> = {
    0: 'inWorkIcon',
    1: 'freeIcon',
    2: 'brokenIcon',
    3: 'decommissionIcon',
};

export const InventoryCardActions = ({
    status,
    warehouses = [],
    hasFullAccess = true,
    canMove = true,
    onCopyClick,
    onDeleteClick,
    onMoveClick,
    onStatusClick,
}: InventoryCardActionsProps) => {
    const { t } = useTranslation();
    const [isMoveMode, setIsMoveMode] = useState(false);
    const { inventoryStatusesData } = useEquipmentInventoryStatuses();
    const availableStatuses = inventoryStatusesData.filter((item) => item.status !== status);

    const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            setIsMoveMode(false);
        }
    };

    return (
        <UiDropdown
            placement="right-start"
            className={cn(cls.actionsMenu, { [cls.actionsMenuMove]: isMoveMode })}
            onOpenChange={handleOpenChange}
            renderTrigger={({ ref, referenceProps, isOpen }) => (
                <button
                    ref={ref}
                    type="button"
                    className={cn(cls.editInventoryButton, {
                        [cls.editInventoryButtonOpen]: isOpen,
                    })}
                    {...referenceProps}
                    onClick={(event) => {
                        event.stopPropagation();
                        referenceProps.onClick?.(event);
                    }}
                />
            )}
        >
            {({ close }) =>
                isMoveMode ? (
                    <UiDropdownSearchList
                        title={t('QuickEditInventoryModal.SearchModal.titleText')}
                        items={warehouses}
                        onClose={close}
                        onItemClick={(warehouse) => onMoveClick?.(warehouse, close)}
                    />
                ) : (
                    <UiDropdown.List>
                        {canMove && (
                            <UiDropdown.ListItem
                                closeOnClick={false}
                                className={cls.actionsMenuItem}
                                data-id="move-inventory"
                                onClick={() => setIsMoveMode(true)}
                            >
                                <Icon iconName="inventoryMove" width={12} height={12} />
                                <UiText variant="subPlain">
                                    {t('QuickEditInventoryModal.QuickEditButton.move')}
                                </UiText>
                            </UiDropdown.ListItem>
                        )}
                        {hasFullAccess && (
                            <UiDropdown.ListItem
                                className={cls.actionsMenuItem}
                                data-id="copy-inventory"
                                onClick={onCopyClick}
                            >
                                <Icon iconName="inventoryCopy" width={12} height={12} />
                                <UiText variant="subPlain">
                                    {t('QuickEditInventoryModal.QuickEditButton.copy')}
                                </UiText>
                            </UiDropdown.ListItem>
                        )}
                        {availableStatuses.map((statusItem) => (
                            <UiDropdown.ListItem
                                key={statusItem.status}
                                className={cls.actionsMenuItem}
                                data-id={statusItem.status}
                                onClick={onStatusClick?.(statusItem.status)}
                            >
                                <Icon
                                    iconName={inventoryStatusIconNames[statusItem.status]}
                                    width={12}
                                    height={12}
                                    fill={statusItem.color}
                                />
                                <UiText variant="subPlain">{statusItem.text}</UiText>
                            </UiDropdown.ListItem>
                        ))}
                        {status === 3 && hasFullAccess && (
                            <UiDropdown.ListItem
                                className={cn(cls.actionsMenuItem, cls.deleteAction)}
                                data-id="remove-inventory"
                                onClick={onDeleteClick}
                            >
                                <Icon iconName="inventoryDelete" width={12} height={12} />
                                <UiText variant="subPlain">
                                    {t('QuickEditInventoryModal.QuickEditButton.remove')}
                                </UiText>
                            </UiDropdown.ListItem>
                        )}
                    </UiDropdown.List>
                )
            }
        </UiDropdown>
    );
};
